import { EventType } from "@/analytics/analytics-events";
import { useAppDispatch } from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import {
  ContextualToolbar,
  FaroTooltip,
  TagsDeleteIcon,
  ToolButton,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Stack } from "@mui/system";
import { useCallback, type JSX } from "react";
import { useTagsManagementContext } from "./tags-management-context";
import { TagsManagementViewToolbar } from "./tags-management-view-toolbar";
import { AddTagsButton } from "./ui-components/add-tags-button";
import { useRemoveAllTagsFromIElements } from "./use-remove-all-tags";

/**
 *
 * @returns The HTML overlay elements of the TagsManagement mode
 */
export function TagsManagementOverlay(): JSX.Element | null {
  const { selectedIds, clearSelection } = useTagsManagementContext();

  const removeAllTags = useRemoveAllTagsFromIElements(selectedIds);
  const dispatch = useAppDispatch();

  const onClearSelection = useCallback(() => {
    Analytics.track(EventType.clearScansSelection);
    clearSelection();
  }, [clearSelection]);

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        p: 1,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "flex-start",
          justifyContent: "flex-end",
          flexGrow: 1,
        }}
      >
        <TagsManagementViewToolbar />
      </Stack>
      {selectedIds.length > 0 && (
        <Stack
          direction="column"
          sx={{
            p: 1,
            justifyContent: "end",
            alignItems: "center",
          }}
        >
          <ContextualToolbar
            label={
              <>
                <TranslateVar name="numberOfElements">
                  {selectedIds.length}
                </TranslateVar>{" "}
                {`${selectedIds.length > 1 ? "elements" : "element"} selected`}
              </>
            }
            clearSelection={onClearSelection}
          >
            <AddTagsButton selectedIds={selectedIds} />
            <FaroTooltip title="Remove tags" placement="top">
              <ToolButton
                aria-label="delete-tag"
                onClick={async () => {
                  Analytics.track(EventType.removeAllTagsFromScan);
                  dispatch(setShowSpinner(true));
                  await removeAllTags();
                  dispatch(setShowSpinner(false));
                }}
              >
                <TagsDeleteIcon />
              </ToolButton>
            </FaroTooltip>
          </ContextualToolbar>
        </Stack>
      )}
    </Stack>
  );
}
